/**
 * The smallest router that does the job.
 *
 * Path-based, because these are real URLs people are sent to in email
 * (`/verify-email?token=…`) and bookmark. The Worker serves `index.html` for
 * any unmatched path, so a deep link loads the app and this hook reads the
 * path it landed on.
 */
import { useCallback, useEffect, useState } from "react";

export interface Route {
  path: string;
  query: URLSearchParams;
}

function read(): Route {
  return { path: window.location.pathname, query: new URLSearchParams(window.location.search) };
}

export function useRoute(): [Route, (to: string) => void] {
  const [route, setRoute] = useState<Route>(read);

  const navigate = useCallback((to: string) => {
    const url = to.startsWith("http") ? new URL(to) : new URL(to, window.location.origin);
    const next = `${url.pathname}${url.search}`;
    if (next === `${window.location.pathname}${window.location.search}`) {
      window.scrollTo(0, 0);
      return;
    }
    window.history.pushState({}, "", next);
    setRoute(read());
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onPop = () => setRoute(read());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest?.("a");
      if (!anchor || anchor.target === "_blank" || anchor.hasAttribute("download")) return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
      let url: URL;
      try { url = new URL(href, window.location.origin); } catch { return; }
      if (url.origin !== window.location.origin) return;
      event.preventDefault();
      navigate(`${url.pathname}${url.search}${url.hash}`);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate]);

  return [route, navigate];
}
